import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const SECTOR_LABELS = {
  healthcare: "Healthcare",
  education: "Education",
  "b2b-industry": "B2B Industry",
  "real-estate": "Real Estate",
  retail: "Retail & E-Com",
  agriculture: "Agriculture",
  hospitality: "Hospitality",
  legal: "Legal Tech",
  finance: "FinTech",
  sports: "Sports Tech",
  govtech: "Civic Tech",
};

export const Draggable3DPhotoRing = ({ items = [] }) => {
  const containerRef = useRef(null);
  const ringRef = useRef(null);

  // Take at most 6 items for the 60deg cylindrical 3D ring
  const ringItems = items.slice(0, 6);
  const count = ringItems.length || 6;
  const stepAngle = 360 / count;

  // Sizing tuned so images and cards are large, prominent, and fully visible
  const [dimensions, setDimensions] = useState({
    cardWidth: 320,
    cardHeight: 430,
    radius: 430,
    containerHeight: 500,
  });

  useEffect(() => {
    const updateDimensions = () => {
      const w = window.innerWidth;
      if (w < 640) {
        // Mobile
        setDimensions({
          cardWidth: 220,
          cardHeight: 315,
          radius: 260,
          containerHeight: 375,
        });
      } else if (w < 1024) {
        // Tablet
        setDimensions({
          cardWidth: 270,
          cardHeight: 380,
          radius: 350,
          containerHeight: 440,
        });
      } else {
        // Desktop - enlarged for clear image visibility
        setDimensions({
          cardWidth: 320,
          cardHeight: 430,
          radius: 430,
          containerHeight: 500,
        });
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  // Motion physics state in refs for smooth 60fps/120fps direct DOM manipulation
  const rotationRef = useRef(0);
  const velocityRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isHoveredRef = useRef(false);
  const startXRef = useRef(0);
  const lastXRef = useRef(0);
  const lastTimeRef = useRef(0);
  const dragDistanceRef = useRef(0);

  // Directly update DOM transform to avoid React re-rendering every frame
  const applyTransform = useCallback((rot) => {
    if (ringRef.current) {
      ringRef.current.style.transform = `translateZ(-${dimensions.radius}px) rotateY(${rot}deg)`;
    }
  }, [dimensions.radius]);

  // Main animation loop: momentum throwing & continuous smooth idle display rotation
  useEffect(() => {
    let rafId;

    const tick = () => {
      if (!isDraggingRef.current) {
        if (Math.abs(velocityRef.current) > 0.05) {
          // Momentum throw with smooth friction damping
          rotationRef.current += velocityRef.current;
          velocityRef.current *= 0.94; // friction coefficient
        } else {
          // Smooth, active display-case speed
          velocityRef.current = 0;
          const idleSpeed = isHoveredRef.current ? 0.06 : 0.18;
          rotationRef.current += idleSpeed;
        }
        applyTransform(rotationRef.current);
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [applyTransform]);

  // Pointer event handlers for draggable throw interaction
  const handlePointerDown = (e) => {
    isDraggingRef.current = true;
    dragDistanceRef.current = 0;
    startXRef.current = e.clientX;
    lastXRef.current = e.clientX;
    lastTimeRef.current = performance.now();
    velocityRef.current = 0;

    if (containerRef.current) {
      containerRef.current.style.cursor = "grabbing";
    }
  };

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current) return;
    const now = performance.now();
    const deltaX = e.clientX - lastXRef.current;
    const deltaTime = Math.max(now - lastTimeRef.current, 1);

    dragDistanceRef.current += Math.abs(deltaX);

    // Drag sensitivity
    const sensitivity = window.innerWidth < 640 ? 0.38 : 0.28;
    rotationRef.current += deltaX * sensitivity;

    // Instantaneous velocity (deg/frame)
    const instantVelocity = (deltaX / deltaTime) * 16.67 * sensitivity;
    velocityRef.current = velocityRef.current * 0.35 + instantVelocity * 0.65;

    lastXRef.current = e.clientX;
    lastTimeRef.current = now;

    applyTransform(rotationRef.current);
  };

  const handlePointerUp = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;

    // Cap maximum momentum throw speed for responsive feel
    const MAX_VELOCITY = 14;
    velocityRef.current = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, velocityRef.current));

    if (containerRef.current) {
      containerRef.current.style.cursor = "grab";
    }
  };

  // Step button handler (Prev / Next) with smooth impulse
  const stepTo = (direction) => {
    velocityRef.current = 0;
    const targetDelta = direction * stepAngle;
    velocityRef.current = targetDelta * 0.09;
  };

  return (
    <div className="w-full relative select-none">
      {/* Outer 3D Stage with Side Arrow Buttons */}
      <div className="relative w-full overflow-hidden">
        {/* Left Arrow Button on Left Side of Ring */}
        <button
          type="button"
          onClick={() => stepTo(1)}
          className="absolute left-2 sm:left-4 lg:left-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/95 hover:bg-[#FF5A1F] text-[#0B0B0B] hover:text-white shadow-[0_6px_20px_rgba(0,0,0,0.12)] border border-black/10 flex items-center justify-center transition-all duration-300 cursor-pointer backdrop-blur-md hover:scale-105 active:scale-95"
          aria-label="Previous project"
        >
          <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>

        {/* Right Arrow Button on Right Side of Ring */}
        <button
          type="button"
          onClick={() => stepTo(-1)}
          className="absolute right-2 sm:right-4 lg:right-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/95 hover:bg-[#FF5A1F] text-[#0B0B0B] hover:text-white shadow-[0_6px_20px_rgba(0,0,0,0.12)] border border-black/10 flex items-center justify-center transition-all duration-300 cursor-pointer backdrop-blur-md hover:scale-105 active:scale-95"
          aria-label="Next project"
        >
          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>

        {/* 3D Viewport Stage */}
        <div
          ref={containerRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onMouseEnter={() => {
            isHoveredRef.current = true;
          }}
          onMouseLeave={() => {
            isHoveredRef.current = false;
            handlePointerUp();
          }}
          className="w-full relative overflow-visible cursor-grab touch-pan-y"
          style={{
            height: `${dimensions.containerHeight}px`,
            perspective: "1300px",
            perspectiveOrigin: "50% 50%",
          }}
          aria-label="Draggable 3D photo ring showcasing flagship projects"
        >
          {/* Subtle radial floor ambient glow */}
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[340px] bg-gradient-to-r from-[#FF5A1F]/10 via-[#FF5A1F]/12 to-amber-500/10 rounded-full blur-[90px] pointer-events-none"
            style={{ transform: "rotateX(75deg)" }}
          />

          {/* 3D Ring Cylinder */}
          <div
            ref={ringRef}
            className="absolute left-1/2 top-1/2"
            style={{
              transformStyle: "preserve-3d",
              transform: `translateZ(-${dimensions.radius}px) rotateY(0deg)`,
              width: "0px",
              height: "0px",
            }}
          >
            {ringItems.map((p, i) => {
              const cardAngle = i * stepAngle;

              const isPainEasy = p.slug === "paineasy" || p.domain?.includes("paineasy") || p.title?.toLowerCase().includes("paineasy");
              const isMedyLife = p.slug === "meadylife" || p.slug === "medylife" || p.domain?.includes("medylife") || p.title?.toLowerCase().includes("meady") || p.title?.toLowerCase().includes("medy");
              const displayTitle = isMedyLife ? "MedyLife" : (isPainEasy ? "PainEasy" : p.title);

              let logoUrl = p.logo;
              if (isPainEasy) {
                logoUrl = "/brand/paineasy-logo.svg";
              } else if (isMedyLife) {
                logoUrl = "/brand/medylife-logo.png";
              } else if (p.slug === "mycitydekho" || p.title?.toLowerCase().includes("citydekho")) {
                logoUrl = "/brand/citydekho-pin.png";
              } else if (p.slug === "ecopro-builders" || p.title?.toLowerCase().includes("ecopro")) {
                logoUrl = "/brand/ecopro-logo.png";
              } else if (!logoUrl && p.domain) {
                logoUrl = `https://www.google.com/s2/favicons?domain=${p.domain}&sz=128`;
              }

              return (
                <div
                  key={p.id || p.slug || i}
                  className="absolute"
                  style={{
                    width: `${dimensions.cardWidth}px`,
                    height: `${dimensions.cardHeight}px`,
                    left: `-${dimensions.cardWidth / 2}px`,
                    top: `-${dimensions.cardHeight / 2}px`,
                    transform: `rotateY(${cardAngle}deg) translateZ(${dimensions.radius}px)`,
                    transformStyle: "preserve-3d",
                    backfaceVisibility: "visible",
                  }}
                >
                  {/* Clean Photo Card - Enlarged for crystal-clear image visibility */}
                  <div
                    className="w-full h-full rounded-[1.75rem] bg-white border border-black/10 shadow-[0_16px_40px_-10px_rgba(0,0,0,0.1),0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_22px_48px_-10px_rgba(255,90,31,0.22),0_6px_16px_rgba(0,0,0,0.06)] hover:border-[#FF5A1F]/40 transition-all duration-300 p-3.5 sm:p-4 flex flex-col justify-between group overflow-hidden"
                    onClick={(e) => {
                      if (dragDistanceRef.current > 8) {
                        e.preventDefault();
                      }
                    }}
                  >
                    {/* Image Container - Generous size so images show properly and clearly */}
                    <div className="relative w-full aspect-[16/10] rounded-xl overflow-hidden bg-neutral-50/90 border border-black/[0.08] shrink-0 shadow-inner flex items-center justify-center p-1 sm:p-1.5">
                      <img
                        src={p.img}
                        alt={displayTitle}
                        loading="eager"
                        draggable={false}
                        className="w-full h-full object-contain object-center rounded-lg group-hover:scale-[1.02] transition-transform duration-500 select-none"
                        onError={(e) => {
                          e.target.onerror = null;
                          if (isPainEasy) {
                            e.target.src = "/brand/paineasy-preview.png";
                          } else if (isMedyLife) {
                            e.target.src = "/brand/medylife-preview.png";
                          } else {
                            e.target.src = "/brand/launchpad.png";
                          }
                        }}
                      />

                      {/* Sector / Category Tag Pill */}
                      <span className="absolute top-2.5 right-2.5 text-[9px] sm:text-[10px] font-semibold px-2.5 py-0.5 rounded-full bg-[#FF5A1F] text-white shadow-sm pointer-events-none tracking-wide z-10">
                        {SECTOR_LABELS[p.sector] || (p.sector ? p.sector.replace(/-/g, " ") : p.cat || "Web")}
                      </span>
                    </div>

                    {/* Card Content */}
                    <div className="flex flex-col justify-between flex-1 pt-3 pb-0.5 px-0.5">
                      <div>
                        {/* Logo + Title */}
                        <div className="flex items-center gap-2 mb-1.5">
                          {logoUrl && (
                            <img
                              src={logoUrl}
                              alt={displayTitle}
                              draggable={false}
                              className="w-5 h-5 rounded object-contain shrink-0 border border-black/10 select-none shadow-xs bg-white p-0.5"
                              onError={(e) => {
                                if (isPainEasy) {
                                  e.target.src = "/brand/paineasy-logo.svg";
                                } else if (isMedyLife) {
                                  e.target.src = "/brand/medylife-logo.png";
                                } else {
                                  e.target.style.display = "none";
                                }
                              }}
                            />
                          )}
                          <h3 className="font-heading text-base sm:text-lg font-bold text-[#0B0B0B] group-hover:text-[#FF5A1F] transition-colors truncate">
                            {displayTitle}
                          </h3>
                        </div>

                        {/* Description */}
                        <p className="text-neutral-500 text-xs sm:text-[13px] line-clamp-2 leading-relaxed mb-2">
                          {p.desc}
                        </p>
                      </div>

                      {/* Bottom Action Row */}
                      <div className="pt-2 border-t border-black/5 flex items-center justify-end mt-auto text-xs sm:text-sm">
                        <Link
                          to={`/portfolio/${p.slug}`}
                          className="inline-flex items-center gap-1.5 text-[#FF5A1F] font-semibold group-hover:translate-x-1 transition-transform"
                          onClick={(e) => {
                            if (dragDistanceRef.current > 8) e.preventDefault();
                          }}
                        >
                          <span>Case Study</span>
                          <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
